import type {
  CircleLayerSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  Map as MapLibreMap,
  SymbolLayerSpecification,
} from "maplibre-gl";

import { MAP_THEME } from "./map-theme";

/** Shared checkpoint pin styling — viewport-aligned so pins stay visible on MapTiler hybrid terrain. */
export const CHECKPOINT_PIN_PAINT: NonNullable<CircleLayerSpecification["paint"]> = {
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 7, 12, 12, 16, 16],
  "circle-color": ["coalesce", ["get", "color"], MAP_THEME.checkpointStatus.default],
  "circle-stroke-width": 3,
  "circle-stroke-color": "#ffffff",
  "circle-opacity": 1,
  "circle-pitch-alignment": "viewport",
};

/**
 * PRD-001Y Wave C confirmed field pin (routed and unrouted). Same geometry as the historic
 * checkpoint pin, but the RING is data-driven rather than hardcoded white, because the taxonomy
 * needs it: when a non-active operational status takes over the fill, the ring carries the
 * placement bucket so a routed and an unrouted pin in maintenance do not render identically.
 */
export const FIELD_PIN_PAINT: NonNullable<CircleLayerSpecification["paint"]> = {
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 7, 12, 12, 16, 16],
  "circle-color": ["coalesce", ["get", "color"], MAP_THEME.checkpointStatus.default],
  "circle-stroke-width": 3,
  "circle-stroke-color": ["coalesce", ["get", "strokeColor"], MAP_THEME.fieldPin.defaultStroke],
  "circle-opacity": 1,
  "circle-pitch-alignment": "viewport",
};

/**
 * Draft variant. Identical geometry so it stays clickable at property zoom, with a slightly
 * translucent fill on top of the violet ring, so "not confirmed yet" survives both a
 * colour-blind read and a quick glance. Shared by the Property map and CAD so the two cannot
 * drift.
 */
export const FIELD_PIN_DRAFT_PAINT: NonNullable<CircleLayerSpecification["paint"]> = {
  ...FIELD_PIN_PAINT,
  "circle-color": ["coalesce", ["get", "color"], MAP_THEME.fieldPin.draft],
  "circle-stroke-color": ["coalesce", ["get", "strokeColor"], MAP_THEME.fieldPin.draftStroke],
  "circle-opacity": 0.85,
};

/** Boundary draw handles — same viewport alignment as checkpoint pins on hybrid satellite. */
export const DRAFT_BOUNDARY_VERTEX_PAINT: NonNullable<CircleLayerSpecification["paint"]> = {
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 7, 12, 11, 16, 15],
  "circle-color": "#5b8fff",
  "circle-stroke-width": 3,
  "circle-stroke-color": "#ffffff",
  "circle-opacity": 1,
  "circle-pitch-alignment": "viewport",
};

/** Update an existing GeoJSON source or create it when the basemap style was reset. */
export function syncGeoJsonSource(map: MapLibreMap, sourceId: string, data: GeoJSON.FeatureCollection): void {
  const existing = map.getSource(sourceId);
  if (existing !== undefined && "setData" in existing && typeof existing.setData === "function") {
    existing.setData(data);
    return;
  }
  map.addSource(sourceId, { type: "geojson", data });
}

/** Add a GeoJSON source when missing. Layer creation is separate so a failed addLayer cannot strand a source. */
export function ensureGeoJsonSource(
  map: MapLibreMap,
  sourceId: string,
  data: GeoJSON.FeatureCollection,
): void {
  if (map.getSource(sourceId) !== undefined) {
    return;
  }
  map.addSource(sourceId, { type: "geojson", data });
}

/** Add a layer when missing. If present, re-apply filter so Wave C taxonomy filters stick after
 * style reloads that drop expressions. */
export function ensureMapLayer(
  map: MapLibreMap,
  layer: CircleLayerSpecification | FillLayerSpecification | LineLayerSpecification | SymbolLayerSpecification,
  beforeId?: string,
): void {
  if (map.getLayer(layer.id) !== undefined) {
    if ("filter" in layer && layer.filter !== undefined) {
      try {
        map.setFilter(layer.id, layer.filter);
      } catch {
        // Filter expressions may disagree mid-style load; next idle refresh retries.
      }
    }
    return;
  }
  try {
    if (beforeId !== undefined) {
      map.addLayer(layer, beforeId);
      return;
    }
    map.addLayer(layer);
  } catch {
    try {
      map.addLayer(layer);
    } catch {
      // Style may be mid-mutation; the next idle refresh will retry.
    }
  }
}

/** First label/symbol layer in the basemap. */
export function findFirstSymbolLayerId(map: MapLibreMap): string | undefined {
  const layers = map.getStyle()?.layers;
  if (layers === undefined) {
    return undefined;
  }
  for (const layer of layers) {
    if (layer.type === "symbol") {
      return layer.id;
    }
  }
  return undefined;
}

/**
 * MapTiler hybrid can place an early symbol layer above rasters. Anchor custom overlays after
 * the last raster/hillshade layer and before the next symbol layer when possible.
 */
export function findOverlayInsertBeforeId(map: MapLibreMap): string | undefined {
  const layers = map.getStyle()?.layers;
  if (layers === undefined) {
    return undefined;
  }
  let lastRasterIndex = -1;
  for (let index = 0; index < layers.length; index += 1) {
    const type = layers[index]?.type;
    if (type === "raster" || type === "hillshade") {
      lastRasterIndex = index;
    }
  }
  for (let index = lastRasterIndex + 1; index < layers.length; index += 1) {
    if (layers[index]?.type === "symbol") {
      return layers[index]?.id;
    }
  }
  return findFirstSymbolLayerId(map);
}

/** Move operator overlays to the top of the style stack (above late-loading MapTiler rasters). */
export function raiseMapOverlayLayersToTop(map: MapLibreMap, layerIds: readonly string[]): void {
  for (const layerId of layerIds) {
    if (map.getLayer(layerId) === undefined) {
      continue;
    }
    try {
      map.moveLayer(layerId);
    } catch {
      // Style may be mid-mutation; the next idle refresh will retry.
    }
  }
}

/** Insert operator overlays above satellite rasters and below road/POI labels. */
export function repositionMapOverlayLayers(map: MapLibreMap, layerIds: readonly string[]): void {
  const beforeId = findOverlayInsertBeforeId(map);
  for (const layerId of layerIds) {
    if (map.getLayer(layerId) === undefined) {
      continue;
    }
    try {
      if (beforeId !== undefined) {
        map.moveLayer(layerId, beforeId);
      } else {
        map.moveLayer(layerId);
      }
    } catch {
      // Style may be mid-mutation; the next idle refresh will retry.
    }
  }
  raiseMapOverlayLayersToTop(map, layerIds);
}

/** @deprecated Prefer repositionMapOverlayLayers — kept for call sites that only need a z-index bump. */
export function raiseMapOverlayLayers(map: MapLibreMap, layerIds: readonly string[]): void {
  repositionMapOverlayLayers(map, layerIds);
}

/** Run overlay refresh once the basemap style is ready; retries on idle while MapTiler is still loading. */
export function runMapOverlayRefresh(map: MapLibreMap, refresh: () => void): void {
  const attempt = (): void => {
    if (!map.isStyleLoaded()) {
      map.once("idle", attempt);
      return;
    }
    refresh();
  };
  attempt();
}

/** Run after the basemap style is ready (MapTiler loads asynchronously). */
export function whenMapStyleReady(map: MapLibreMap, run: () => void): void {
  if (map.isStyleLoaded()) {
    run();
    return;
  }
  const onIdle = (): void => {
    if (!map.isStyleLoaded()) {
      return;
    }
    map.off("idle", onIdle);
    run();
  };
  map.on("idle", onIdle);
}

/**
 * MapTiler hybrid styles can append raster/label layers after the first `load` event. Re-raise
 * operator overlays on idle/styledata so checkpoint circles stay above the basemap.
 */
export function watchCadOverlayStack(
  map: MapLibreMap,
  layerIds: readonly string[],
  restore?: () => void,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const bump = (): void => {
    if (timer !== null) {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      requestAnimationFrame(() => {
        if (!map.isStyleLoaded()) {
          map.once("idle", bump);
          return;
        }
        if (restore !== undefined) {
          restore();
        } else {
          repositionMapOverlayLayers(map, layerIds);
        }
      });
    }, 0);
  };

  map.on("idle", bump);
  map.on("styledata", bump);
  bump();

  return () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    map.off("idle", bump);
    map.off("styledata", bump);
  };
}
